/* eslint-disable @typescript-eslint/no-unused-vars */ // 333 - 340
import { ApplicationAPI } from './../../types/salesforce/api/application/application-api';
import { Catalogue } from './../../types/catalogue';
import {
  Application,
  PaymentMethodAPI,
  PaymentMethodCode,
  ProductDetailsAPI,
  SolutionType,
} from '../../types';
import { PageName as OrderPageName } from '../../../order/pages/page-names';
import { PageName as DeviceActivationPageName } from 'src/features/device-activation/pages/page-names';
import {
  chooseSolutionRoute,
  orderOutcomeRoute,
  orderSummaryRoute,
  orderPaymentRoute,
  orderPaymentStatusRoute,
  paymentTypeRoute,
  setupComplete,
  yourDetailsRoute,
  assignDevicesRoute,
  processPaymentRoute,
} from '../../../order/helpers/routes';
import { PageName as OnboardingPageName, PageName } from '../../../onboarding/pages/page-names';
import {
  bankDetails,
  businessDetails,
  companyDetails,
  confirmAddress,
  directorDetails,
  personalDetails,
  personalSelfie,
  residentialAddress,
  uploadDocuments,
} from '../../../onboarding/helpers/routes';
import { ApplicationStep } from '../../types/application/application';
import { Delivery, Solution } from '../../types/application/opportunity';
import { getSubtractedSolutions } from '../../../order/helpers/delivery-helpers';
import { GroupName } from '../../../onboarding/pages/group-names';
import { PaymentStatus } from '../../types/application/payment';
import { getApplicationAPIFromApplication } from '../../helpers/dto-mappers/salesforce/get-application-api-from-application';
import {
  assignNameRoute,
  scanDeviceRoute,
  setupAccountRoute,
} from 'src/features/device-activation/helpers/routes';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { isTenderType } from '../../helpers';
import { calculateTotalCartQuantity } from '@vfs-digital-channels/ns-utils';
import { MAX_NUMBER_OF_TRIES } from 'src/common';
import { Environment } from 'src/Environment';

export function getCurrentStepIndex(steps: ApplicationStep[], pageName: string): number {
  const stepIndex = steps.findIndex((step) => step.pageName === pageName);

  if (stepIndex >= 0) {
    return getNextEnabledStepIndex(steps, stepIndex, false) || stepIndex;
  }

  throw new Error(`App reducer revamp: unknown page name: ${pageName}`);
}

export function getStateWithStepEnabled(
  pageName: string,
  enabled: boolean,
  state: Application,
  hideStep?: boolean,
): Application {
  const currentStep = (state.steps || [])[state.currentStepIndex || 0];

  if (!currentStep) {
    throw new Error(`App reducer revamp: current step invalid: ${state.currentStepIndex}`);
  }

  const stepsNew = (state.steps || []).map((step) => {
    if (step.pageName === pageName) {
      return {
        ...step,
        disabled: !enabled,
        hidden: hideStep,
      };
    } else {
      return step;
    }
  });

  return {
    ...state,
    steps: stepsNew,
    currentStepIndex: getCurrentStepIndex(stepsNew, currentStep.pageName),
  };
}

/**
 Searches array of steps and returns the first enable step
  @param steps - array of steps
  @param stepIndex - step index search parameter
  @param searchFromStepIndexExclusive - if `false`, searching starts
  from index specified in `stepIndex` parameter, else it starts from `stepIndex` + 1.
  Defaults to `true`.
  @returns stepIndex - searching array indexes in ascending order, returns the first enabled
  step
 */
export function getNextEnabledStepIndex(
  steps?: ApplicationStep[],
  stepIndex?: number,
  searchFromStepIndexExclusive: boolean = true,
) {
  if (steps !== undefined && stepIndex !== undefined) {
    const stepsFiltered = steps
      .map((step, index) => {
        return {
          ...step,
          index: index,
        };
      })
      .slice(searchFromStepIndexExclusive ? stepIndex + 1 : stepIndex);
    const result = stepsFiltered.find((applicationStep) => !applicationStep.disabled);
    return result ? result.index : stepIndex;
  }

  return stepIndex;
}

/**
 @param steps - array of steps
 @param stepIndex - step index search parameter
 @returns stepIndex - searching array indexes in decending order, returns the first enabled
 step with index less than the `stepIndex` parameter
 */
export function getPreviousEnabledStepIndex(steps?: ApplicationStep[], stepIndex?: number) {
  if (steps !== undefined && stepIndex !== undefined) {
    const stepsFiltered = steps
      .map((step, index) => {
        return {
          ...step,
          index: index,
        };
      })
      .reverse()
      .slice(steps.length - stepIndex);

    const result = stepsFiltered.find((applicationStep) => !applicationStep.disabled);
    return result ? result.index : stepIndex;
  }

  return stepIndex;
}

export function getStateAfterNavigateToNextStep(state: Application) {
  if (
    state.currentStepIndex !== undefined &&
    state.steps !== undefined &&
    state.currentStepIndex <= state.steps.length
  ) {
    return {
      ...state,
      currentStepIndex: getNextEnabledStepIndex(state.steps, state.currentStepIndex),
    };
  }
  return state;
}

export function getStateAfterNavigateToPreviousStep(state: Application) {
  if (
    state.currentStepIndex !== undefined &&
    state.steps !== undefined &&
    state.currentStepIndex > 0
  ) {
    return {
      ...state,
      currentStepIndex: getPreviousEnabledStepIndex(state.steps, state.currentStepIndex),
    };
  }
  return state;
}

export const orderJourneySteps = [
  {
    groupName: OrderPageName.CHOOSE_SOLUTION,
    pageName: OrderPageName.CHOOSE_SOLUTION,
    route: chooseSolutionRoute,
    hidden: !Environment.current.disabledFeatures.revamp, // Used to hide step on progress bar
  },
  {
    groupName: Environment.current.disabledFeatures.revamp
      ? OrderPageName.PAYMENT_TYPE
      : OrderPageName.PAYMENT_TYPE_REVAMP,
    pageName: Environment.current.disabledFeatures.revamp
      ? OrderPageName.PAYMENT_TYPE
      : OrderPageName.PAYMENT_TYPE_REVAMP,
    route: paymentTypeRoute,
    disabled: true,
    hidden: true,
  },
  {
    pageName: OrderPageName.YOUR_DETAILS,
    route: yourDetailsRoute,
  },
  {
    pageName: OrderPageName.ORDER_SUMMARY,
    route: orderSummaryRoute,
  },
  {
    groupName: OrderPageName.PAY,
    pageName: OrderPageName.PROCESS_PAYMENT,
    route: processPaymentRoute,
    disabled: true,
  },
  {
    groupName: OrderPageName.PAY,
    pageName: OrderPageName.PAY,
    route: orderOutcomeRoute,
  },
  {
    groupName: OrderPageName.PAY,
    pageName: OrderPageName.PAY_ONLINE,
    route: orderPaymentRoute,
    disabled: true,
  },
  {
    groupName: OrderPageName.PAY,
    pageName: OrderPageName.PAY_STATUS,
    route: orderPaymentStatusRoute,
    disabled: true,
  },
];

export const deviceActivationSteps = [
  {
    pageName: DeviceActivationPageName.YOUR_DETAILS,
    route: yourDetailsRoute,
  },
  {
    pageName: DeviceActivationPageName.SCAN_DEVICE,
    route: scanDeviceRoute,
  },
  {
    pageName: DeviceActivationPageName.ASSIGN_NAME,
    route: assignNameRoute,
  },
  {
    groupName: DeviceActivationPageName.CREATE_PROFILE,
    pageName: DeviceActivationPageName.SETUP_YOUR_ACCOUNT,
    route: setupAccountRoute,
  },
];

export const onboardingJourneySteps = [
  {
    groupName: GroupName.COMPANY_DETAILS,
    pageName: OnboardingPageName.SETUP_YOUR_ACCOUNT,
    route: companyDetails,
  },
  {
    groupName: GroupName.COMPANY_DETAILS,
    pageName: OnboardingPageName.BUSINESS_DETAILS,
    route: businessDetails,
  },
  {
    groupName: GroupName.COMPANY_DETAILS,
    pageName: OnboardingPageName.DIRECTOR_DETAILS,
    route: directorDetails,
    disabled: true,
  },
  {
    groupName: GroupName.COMPANY_DETAILS,
    pageName: OnboardingPageName.CONFIRM_ADDRESS,
    route: confirmAddress,
  },
  {
    groupName: GroupName.COMPANY_DETAILS,
    pageName: OnboardingPageName.RES_ADDRESS,
    route: residentialAddress,
    disabled: true,
  },
  {
    groupName: GroupName.PERSONAL_DETAILS,
    pageName: OnboardingPageName.PERSONAL_DETAILS,
    route: personalDetails,
  },
  {
    groupName: GroupName.PERSONAL_DETAILS,
    pageName: OnboardingPageName.BANK_DETAILS,
    route: bankDetails,
  },
  {
    groupName: GroupName.PERSONAL_DETAILS,
    pageName: OnboardingPageName.PERSONAL_SELFIE,
    route: personalSelfie,
  },
  {
    groupName: GroupName.UPLOAD_DOCUMENTS,
    pageName: OnboardingPageName.UPLOAD_DOCUMENTS,
    route: uploadDocuments,
  },
  {
    groupName: GroupName.DONE,
    pageName: OnboardingPageName.SETUP_COMPLETION,
    route: setupComplete,
  },
] as ApplicationStep[];

export function getStateAfterOnboardingJourneyStarted(state: Application) {
  const newState = {
    ...state,
    currentStepIndex: 0,
    deviceActivationJourney: false,
    steps: onboardingJourneySteps,
    stage: {
      ...state.stage,
      isOrderComplete: true,
    },
  };

  return getStateWithStepEnabled(
    OnboardingPageName.RES_ADDRESS,
    !!state.creditVetting?.required,
    newState,
  );
}

export function getStateAfterJumpToStep(state: Application, step: number) {
  if (state.steps !== undefined && step <= state.steps.length) {
    return {
      ...state,
      currentStepIndex: step,
    };
  }

  return state;
}

/**
 * The job of this function is to facilitate ensuring that for an application, the total quantities
 * ordered equals the total quantities to be delivered. This may not be the case when the user
 * specifies addresses for all device quantities, but the reduces the total device quantities
 * to less than those to be delivered to said addresses. In this scenario, the function
 * removes one or more solutions from delivery addresses until total ordered equals total
 * to be delivered.
 *
 * @param solutions - the totals for all device quantities ordered.
 * @param deliveries - list of Delivery objects, each specifying a subset of all device quantities.
 * Not all device quantities go to the same address, so the deliveries array specifies which
 * devices go to which addresses. May specify devices that are not included in the total
 * quantities.
 *
 * @returns Delivery array for which the total quantities ordered equals the total quantities
 * to be delivered.
 */
export function getDeliveriesAdjustedToMatchTotalSolutionQuantities(
  solutions: Solution[],
  deliveries: Delivery[],
): Delivery[] {
  const solutionsDeliveries = deliveries.flatMap((delivery) => delivery.solutions);
  const solutionsToBeSubtracted = getSubtractedSolutions(solutions, solutionsDeliveries);

  const solutionsToBeSubtractedFiltered = solutionsToBeSubtracted.filter((solution) => {
    return solution.quantity < 0;
  });

  return solutionsToBeSubtractedFiltered
    .reduce<Delivery[]>(
      (resultDeliveries, solutionToSubtract) => {
        const { productId, acquiringChannel } = solutionToSubtract;
        let quantityLeftToSubtract = Math.abs(solutionToSubtract.quantity);

        return resultDeliveries.map((delivery) => {
          return {
            ...delivery,
            solutions: delivery.solutions
              .map((solution) => {
                if (
                  productId === solution.productId &&
                  acquiringChannel === solution.acquiringChannel
                ) {
                  const quantityToSubtract = Math.min(solution.quantity, quantityLeftToSubtract);
                  quantityLeftToSubtract = quantityLeftToSubtract - quantityToSubtract;
                  return {
                    ...solution,
                    quantity: solution.quantity - quantityToSubtract,
                  };
                }

                return solution;
              })
              .filter((solution) => solution.quantity > 0),
          };
        });
      },
      [...deliveries],
    )
    .filter((delivery) => delivery.solutions.length > 0);
}

/**
 * @param application - the current application state
 * @returns an `Application` object with all onboarding details after the Setup Your Account Page removed
 */
export const resetApplicationState = (application: Application): Application => {
  const { bankDetails, contact, directors, ...rest } = application;
  const {
    residentialAddress,
    isResidentialAddressSameAsBusinessAddress,
    title,
    initials,
    maritalStatus,
    ...contactRest
  } = contact || {};

  const stepIndex: number | undefined = getNextEnabledStepIndex(rest.steps, 1);

  if (stepIndex === undefined) {
    throw new Error(`App reducer revamp: Step Index on application reset error`);
  }

  const newApplication: Application = {
    ...rest,
    solutionType: SolutionType.buyOrRentOnlineFicaOnline,
    currentStepIndex: stepIndex,
    contact: {
      ...contactRest,
    },
    payment: {
      paymentStatus: PaymentStatus.NONE,
      maxNumberOfTries: MAX_NUMBER_OF_TRIES,
    },
    creditVetting: {
      required: application.creditVetting?.required,
    },
    eCommerceRequired: application.eCommerceRequired,
  };

  return newApplication;
};

/** @param steps - `ApplicationStep[]` - steps to look in
 * @param step  - `ApplicationStep` - step to find in `steps`
 * @returns `number` the index of `step` in `steps`
 */
export const getStepIndex = (steps: ApplicationStep[], step: ApplicationStep): number => {
  const index: number = steps.findIndex((s) => s.pageName === step.pageName);

  if (index === undefined) {
    throw new Error(`App reducer revamp: step name not supported: ${step.pageName}`);
  }

  return index;
};

/**
 * @param steps - `ApplicationStep[]` - steps to iterate through
 * @param state - `Application` - previous state of application
 * @param carryOverReturnIndex - `boolean` - if true, will grab the return index from the Quick Edit, otherwise will grab it from the state
 * @returns - `Application` - updated app after navigated to the next quick edit step
 */
export const getStateAfterNextQuickEditStep = (
  steps: ApplicationStep[],
  state: Application,
  carryOverReturnIndex: boolean,
) => {
  const [nextStep, ...newSteps] = steps;
  const stepIndex: number = getStepIndex(state.steps || [], nextStep);

  const updatedApp = getStateAfterJumpToStep(state, stepIndex);

  const returnIndex: number | undefined = carryOverReturnIndex
    ? updatedApp.quickEdit?.returnIndex
    : state.currentStepIndex;

  return {
    ...updatedApp,
    quickEdit: {
      steps: newSteps,
      returnIndex,
    },
  };
};

/**
 *
 * @param state - `Application` : previous state of the application
 * @param catalogue - `Catalogue` : indicates what devices have been selected
 * @returns - `ApplicationAPI` : returns the Application API after the stage in the application has been changed
 */
export const killApplication = (state: Application, catalogue: Catalogue): ApplicationAPI => ({
  ...getApplicationAPIFromApplication(state, catalogue),
  applicationKilled: true,
});

export const getStateWithPendingAppSteps = (app: Application): Application => {
  if (!app.pendingApp?.isPending || !app.pendingApp.pageUpdates) {
    throw new Error(
      `App reducer revamp: Attempting to apply pending apps page filtering when not in pending app: is pending app: ${app.pendingApp?.isPending}, page updates: ${app.pendingApp?.pageUpdates?.length} `,
    );
  }

  const pagesToVisit: string[] = app.pendingApp.pageUpdates.map((pages) =>
    pages.subStepName.toLowerCase(),
  );

  const newSteps = onboardingJourneySteps.map((step) => ({
    ...step,
    disabled:
      step.pageName !== PageName.SETUP_YOUR_ACCOUNT &&
      step.pageName !== PageName.SETUP_COMPLETION &&
      !pagesToVisit.includes(step.pageName.toLowerCase()),
  }));

  return {
    ...app,
    steps: newSteps,
    currentStepIndex: 0,
  };
};

export const getStateForVodacomBusiness = (
  app: Application,
  products: ProductDetailsAPI[],
  referenceNumber: string,
): Application => {
  app.referenceNumber = referenceNumber;

  const kwika = products.find((product) => product.productName === DEVICE_NAMES.VODAPAY_KWIKA);

  if (!kwika) {
    throw new Error('Integration Revamp: kwika not defined');
  }

  const solutions = (app.opportunity?.solutions || []).map((solution) => {
    solution.productId = kwika.productId;

    return solution;
  });

  app.opportunity = {
    ...app.opportunity,
    deliveries: app.opportunity?.deliveries?.map((delivery) => {
      const updatedSolutions = delivery.solutions.map((solution) => {
        solution.productId = kwika.productId;

        return solution;
      });

      return {
        ...delivery,
        solutions: updatedSolutions,
      };
    }),
    solutions,
  };

  return app;
};

export function getStateAfterRemovingAssignDevice(
  pageName: string,
  enabled: boolean,
  state: Application,
): Application {
  const currentSteps = state.steps || [];
  const currentStep = currentSteps[state.currentStepIndex || 0];

  if (!currentStep) {
    throw new Error(`App reducer revamp: current step invalid: ${state.currentStepIndex}`);
  }

  if (!enabled) {
    const newSteps = currentSteps.filter((step) => step.pageName !== pageName);
    return {
      ...state,
      steps: newSteps,
      currentStepIndex: getCurrentStepIndex(newSteps, currentStep.pageName),
    };
  }

  const assignDeviceStepIndex = currentSteps.findIndex((item) => item.pageName === pageName);

  if (assignDeviceStepIndex < 0) {
    currentSteps.splice(3, 0, {
      pageName: OrderPageName.ASSIGN_DEVICES,
      route: assignDevicesRoute,
      disabled: !enabled,
      groupName: OrderPageName.ASSIGN_DEVICES,
    });
  }

  const stepsNew = currentSteps.map((step) => {
    if (step.pageName === pageName) {
      return {
        ...step,
        disabled: !enabled,
      };
    } else {
      return step;
    }
  });

  return {
    ...state,
    steps: stepsNew,
    currentStepIndex: getCurrentStepIndex(stepsNew, currentStep.pageName),
  };
}

export function getSelectablePaymentMethods(
  state: Application,
  catalogue: Catalogue,
  actionName: string,
) {
  const catalogueSelectablePaymentMethods = (catalogue.paymentMethods ?? []).filter(
    (paymentMethod) => paymentMethod.selectable.toLowerCase() === 'yes',
  );

  if (catalogueSelectablePaymentMethods.length === 0) {
    throw new Error(`App reducer revamp(${actionName}): no payment methods found: check terminals`);
  }

  const filteredPaymentMethods = catalogueSelectablePaymentMethods.filter((paymentMethod) =>
    paymentMethod.acquiringChannels.includes(state?.solutionType ?? ''),
  );

  const cataloguePaymentMethodCodes = catalogue.products.reduce(
    (accumulate: PaymentMethodCode[], product) => {
      const allowedPaymentMethods = product.device.allowedPaymentMethods;
      if (allowedPaymentMethods.length && !isTenderType(product.device)) {
        return [...accumulate, ...allowedPaymentMethods];
      }
      return accumulate;
    },
    [],
  );

  const uniqueCataloguePaymentMethodCodes = cataloguePaymentMethodCodes.filter(
    (value, index, array) => array.indexOf(value) === index,
  );

  return filteredPaymentMethods.reduce((accumulate: PaymentMethodAPI[], current) => {
    if (
      uniqueCataloguePaymentMethodCodes.includes(current.paymentMethodCode as PaymentMethodCode)
    ) {
      return [...accumulate, current];
    }
    return accumulate;
  }, []);
}

export function getStateForThirdParty(state: Application, catalogue: Catalogue): Application {
  const selectablePaymentMethods = getSelectablePaymentMethods(
    state,
    catalogue,
    'getStateForThirdParty',
  );

  let newState: Application = {
    ...state,
    selectablePaymentMethodCodes: selectablePaymentMethods,
  };

  const totalCollectionsQuantity = (state.collections ?? []).reduce(
    (total, collection) => total + collection.quantity,
    0,
  );

  const cartWithoutVirtualProds = (state.cart ?? []).filter((cartItem) => !cartItem.isVirtual);
  const totalCartQuantity = calculateTotalCartQuantity(cartWithoutVirtualProds);

  // If all items in the cart are collections then we can disable cash option
  if (totalCartQuantity === totalCollectionsQuantity) {
    const updatedSelectablePaymentMethods = selectablePaymentMethods
      .filter((paymentMethod) => paymentMethod.paymentMethodCode === PaymentMethodCode.CASH)
      .map((paymentMethod) => {
        if (paymentMethod.paymentMethodCode === PaymentMethodCode.CASH) {
          return {
            ...paymentMethod,
            disabled: true,
          };
        }
        return paymentMethod;
      });

    newState = {
      ...newState,
      selectablePaymentMethodCodes: updatedSelectablePaymentMethods,
    };
  }

  // Set number of tries and max number of tries when continuing with OTP
  if (state?.remotePayment && state?.payment) {
    const currNumOfTries = state.payment?.orderNumber
      ? Number(state.payment.orderNumber.slice(-2))
      : undefined;

    newState = {
      ...newState,
      remotePayment: {
        ...state.remotePayment,
        numberOfTries: currNumOfTries ?? 0,
      },
      payment: {
        ...state.payment,
        maxNumberOfTries: getMaximumPaymentNumberOfTries(currNumOfTries ?? 0),
      },
    };
  }

  return newState;
}

/**
 * The job of this function is to get the maximum number of tries a user can attempt to
 * make a payment for a specific "session".
 * So if a user has attempted to pay 7 times the max should be 9 i.e the next multiple of 3(MAX_NUMBER_TRIES).
 *
 * @param {number} numberOfTries - The current number of tries
 *
 * @returns {number} The maximum number of tries that can be done during a current session.
 */
export function getMaximumPaymentNumberOfTries(numberOfTries: number): number {
  if (numberOfTries % MAX_NUMBER_OF_TRIES === 0) {
    return MAX_NUMBER_OF_TRIES * (numberOfTries / MAX_NUMBER_OF_TRIES + 1);
  }
  const multiplier = Math.ceil(numberOfTries / MAX_NUMBER_OF_TRIES);
  return multiplier > 0 ? MAX_NUMBER_OF_TRIES * multiplier : MAX_NUMBER_OF_TRIES;
}

/**
 * The job of this function is to determine whether the user is still in a payment "session".
 * i.e. the number of tries is not divisible by 3(MAX_NUMBER_OF_TRIES).
 * So if a user has attempted to pay 7 times then we can consider the payment "session" active.
 *
 * @param {string} orderNumber - The order number (reference number amended with the number of tries)
 *
 * @returns {boolean} Whether the payment "session" is active or not.
 */
export function isPaymentSessionActive(orderNumber: string): boolean {
  const numOfTries = Number(orderNumber.slice(-2));
  return numOfTries % MAX_NUMBER_OF_TRIES !== 0;
}
